/**
 * EscaletaTab — Escaleta / timeline del evento (P3).
 *
 * Bloques ordenables (drag-drop) con hora · momento · espacio · canciones · nota.
 * Las canciones se ENLAZAN a las peticiones del evento (heredan su MP3/Spotify/YouTube).
 * Las sugerencias de la IA que no son peticiones quedan como texto gris.
 * - "Generar con IA": borrador con peticiones enganchadas por su momento.
 * - Guardar / PDF imprimible para cabina.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Sparkles, Loader2, Plus, Trash2, GripVertical, Save, FileDown, ListOrdered,
  Clock, MapPin, Music, Play, Pause, Search, Link2, X, Users, Calendar, FileText,
  ArrowDownUp, Check,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { API_URL } from '../config';
import { BUCKETS, BUCKET_LABELS, groupByBucket } from '../utils/momentoBuckets';

const FASES = ['Ceremonia', 'Cóctel', 'Banquete', 'Baile'];
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const uid = () => `b-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const emptyBlock = () => ({ id: uid(), titulo: '', fase: '', hora: '', espacio: '', canciones: [], nota: '' });

// Compatibilidad: canciones podían ser strings; normalizar a {titulo} / {peticion_id,titulo,artista}
const normCancion = (c) => (typeof c === 'string' ? { titulo: c } : (c || {}));

// kebab-case para usar el momento en data-testid de forma robusta
const slug = (s) => String(s || '')
  .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
// Agrupa TODAS las peticiones en los 4 momentos canónicos (mismo clasificador
// `guessBucketFromPeticion`/`groupByBucket` que usa la pestaña "Peticiones"),
// conservando el orden original de la lista dentro de cada momento.
const groupPeticionesByMomento = (peticiones) => {
  const byBucket = groupByBucket(peticiones || []);
  return BUCKETS
    .map((b) => ({ momento: BUCKET_LABELS[b], items: byBucket[b] || [] }))
    .filter((g) => g.items.length > 0);
};

/* ---------- Fila de una petición con reproducción/enlaces (reutiliza datos de peticiones) ---------- */
const PeticionRow = ({ pet, onPlay, playingId, testid }) => {
  const isPlaying = playingId && playingId === pet.audio_file_id;
  return (
    <div className="flex items-center gap-2 bg-slate-800/60 rounded px-2 py-1.5 text-sm" data-testid={testid}>
      <Music className="w-3.5 h-3.5 text-purple-400 shrink-0" />
      <span className="text-white truncate flex-1 min-w-0">
        {pet.titulo || pet.title}{(pet.artista || pet.artist) ? <span className="text-slate-400"> — {pet.artista || pet.artist}</span> : null}
        {pet.nota ? <span className="text-amber-400/90 italic"> ({pet.nota})</span> : null}
      </span>
      {pet.audio_file_id && (
        <button type="button" onClick={() => onPlay(pet.audio_file_id)}
          className="text-green-400 hover:text-green-300 shrink-0" title="Reproducir MP3"
          data-testid={testid ? `${testid}-play` : undefined}>
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      )}
      {pet.spotify_url && (
        <a href={pet.spotify_url} target="_blank" rel="noreferrer"
           className="text-[10px] font-semibold text-green-400 border border-green-500/40 rounded px-1.5 py-0.5 hover:bg-green-500/10 shrink-0" title="Abrir en Spotify">Spotify</a>
      )}
      {pet.youtube_url && (
        <a href={pet.youtube_url} target="_blank" rel="noreferrer"
           className="text-[10px] font-semibold text-red-400 border border-red-500/40 rounded px-1.5 py-0.5 hover:bg-red-500/10 shrink-0" title="Abrir en YouTube">YT</a>
      )}
    </div>
  );
};

/* ---------- Resumen del evento (datos ya introducidos, sin IA) ---------- */
const EventSummaryCard = ({ playlist }) => {
  const equipo = [];
  if (playlist.nombre_dj) equipo.push({ rol: 'DJ', nombre: playlist.nombre_dj });
  if (playlist.nombre_asistente) equipo.push({ rol: 'Asistente', nombre: playlist.nombre_asistente });
  if (playlist.nombre_asistente2) equipo.push({ rol: 'Asistente 2', nombre: playlist.nombre_asistente2 });
  const espacios = (playlist.espacios_evento || []).filter((e) => e.fase || e.espacio);
  const horario = (playlist.horario_inicio || playlist.horario_fin)
    ? `${playlist.horario_inicio || ''} - ${playlist.horario_fin || ''}` : '';

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="escaleta-summary">
      <CardContent className="p-4 space-y-3">
        <h3 className="text-white font-semibold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-400" /> Resumen del evento
        </h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">
          {playlist.fecha_evento && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-slate-500" />{playlist.fecha_evento}</span>}
          {playlist.lugar_evento && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-cyan-500" />{playlist.lugar_evento}</span>}
          {horario && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-500" />{horario}</span>}
          {playlist.duracion_baile && <span className="text-slate-400">Baile: {playlist.duracion_baile}</span>}
        </div>

        {equipo.length > 0 && (
          <div data-testid="escaleta-summary-equipo">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Equipo</p>
            <div className="flex flex-wrap gap-2">
              {equipo.map((m, i) => (
                <span key={i} className="text-sm bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-200">
                  <span className="text-slate-500">{m.rol}:</span> {m.nombre}
                </span>
              ))}
            </div>
          </div>
        )}

        {espacios.length > 0 && (
          <div data-testid="escaleta-summary-espacios">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Espacios por fase</p>
            <ul className="space-y-0.5">
              {espacios.map((e, i) => (
                <li key={i} className="text-sm text-slate-300">
                  <span className="text-cyan-400 font-medium">{e.fase}:</span> {e.espacio}
                  {e.nota ? <span className="text-slate-500"> ({e.nota})</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        {playlist.notas && (
          <div data-testid="escaleta-summary-notas">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Notas generales</p>
            <p className="text-sm text-slate-300 whitespace-pre-wrap max-h-40 overflow-y-auto bg-slate-900/50 rounded p-2 border border-slate-700/60">{playlist.notas}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ---------- Peticiones del evento agrupadas por su etiqueta/correspondencia ---------- */
const PeticionesPorMomento = ({ peticiones, onPlay, playingId }) => {
  const grupos = groupPeticionesByMomento(peticiones);
  if (grupos.length === 0) return null;
  const total = grupos.reduce((n, g) => n + g.items.length, 0);
  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="escaleta-peticiones-momento">
      <CardContent className="p-4 space-y-3">
        <h3 className="text-white font-semibold text-lg flex items-center gap-2">
          <Music className="w-5 h-5 text-purple-400" /> Peticiones del evento
          <span className="text-slate-500 font-normal text-sm">({total})</span>
        </h3>
        {grupos.map((g) => (
          <div key={g.momento} data-testid={`escaleta-pet-grupo-${slug(g.momento)}`}>
            <p className="text-sm font-semibold text-purple-300 mb-1">{g.momento} <span className="text-slate-500 font-normal">({g.items.length})</span></p>
            <div className="space-y-1">
              {g.items.map((p, i) => (
                <PeticionRow key={p.id || i} pet={p} onPlay={onPlay} playingId={playingId} testid={`escaleta-pet-${slug(g.momento)}-${i}`} />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

/* ---------- Editor de canciones de un bloque ---------- */
const CancionesEditor = ({ canciones, peticiones, onChange, onPlay, playingId }) => {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');

  const list = (canciones || []).map(normCancion);
  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));
  const addCancion = (c) => { onChange([...list, c]); setQuery(''); setPicking(false); };

  const q = query.trim().toLowerCase();
  const matches = (peticiones || [])
    .filter((p) => p && (p.titulo || p.artista))
    .filter((p) => !q || `${p.titulo} ${p.artista}`.toLowerCase().includes(q))
    .slice(0, 8);

  return (
    <div className="space-y-1.5">
      {list.map((c, idx) => {
        const pet = c.peticion_id ? (peticiones || []).find((p) => p.id === c.peticion_id) : null;
        if (pet) {
          const isPlaying = playingId && playingId === pet.audio_file_id;
          return (
            <div key={idx} className="flex items-center gap-2 bg-slate-800 rounded px-2 py-1.5 text-sm" data-testid={`escaleta-song-linked-${idx}`}>
              <Music className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              <span className="text-white truncate flex-1 min-w-0">
                {pet.titulo}{pet.artista ? <span className="text-slate-400"> — {pet.artista}</span> : null}
                {(c.nota || pet.nota) ? <span className="text-amber-400/90 italic"> ({c.nota || pet.nota})</span> : null}
              </span>
              {pet.audio_file_id && (
                <button
                  type="button" onClick={() => onPlay(pet.audio_file_id)}
                  className="text-green-400 hover:text-green-300 shrink-0" title="Reproducir MP3"
                  data-testid={`escaleta-song-play-${idx}`}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
              )}
              {pet.spotify_url && (
                <a href={pet.spotify_url} target="_blank" rel="noreferrer"
                   className="text-[10px] font-semibold text-green-400 border border-green-500/40 rounded px-1.5 py-0.5 hover:bg-green-500/10 shrink-0" title="Abrir en Spotify">
                  Spotify
                </a>
              )}
              {pet.youtube_url && (
                <a href={pet.youtube_url} target="_blank" rel="noreferrer"
                   className="text-[10px] font-semibold text-red-400 border border-red-500/40 rounded px-1.5 py-0.5 hover:bg-red-500/10 shrink-0" title="Abrir en YouTube">
                  YT
                </a>
              )}
              <button type="button" onClick={() => removeAt(idx)} className="text-slate-500 hover:text-red-400 shrink-0" data-testid={`escaleta-song-remove-${idx}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        }
        // Sugerencia sin petición → texto gris
        return (
          <div key={idx} className="flex items-center gap-2 bg-slate-800/40 rounded px-2 py-1.5 text-sm" data-testid={`escaleta-song-text-${idx}`}>
            <Music className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <span className="text-slate-400 italic truncate flex-1 min-w-0">
              {c.titulo}{c.artista ? ` - ${c.artista}` : ''} <span className="text-[10px] not-italic">(sugerencia)</span>
            </span>
            <button type="button" onClick={() => removeAt(idx)} className="text-slate-500 hover:text-red-400 shrink-0" data-testid={`escaleta-song-text-remove-${idx}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}

      {picking ? (
        <div className="bg-slate-800 border border-slate-700 rounded p-2 space-y-1.5">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar petición del evento…"
              className="bg-slate-900 border-slate-700 text-white text-sm pl-7 h-8"
              data-testid="escaleta-song-search"
            />
          </div>
          <div className="max-h-44 overflow-y-auto space-y-1">
            {matches.length === 0 && (
              <p className="text-xs text-slate-500 px-1 py-1">Sin peticiones que coincidan.</p>
            )}
            {matches.map((p) => (
              <button
                key={p.id} type="button"
                onClick={() => addCancion({ peticion_id: p.id, titulo: p.titulo, artista: p.artista || '' })}
                className="w-full text-left text-sm text-white bg-slate-900 hover:bg-slate-700 rounded px-2 py-1.5 flex items-center gap-2"
                data-testid={`escaleta-song-option-${p.id}`}
              >
                <Link2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                <span className="truncate">{p.titulo}{p.artista ? <span className="text-slate-400"> — {p.artista}</span> : null}</span>
              </button>
            ))}
            {query.trim() && (
              <button
                type="button" onClick={() => addCancion({ titulo: query.trim() })}
                className="w-full text-left text-xs text-slate-400 italic hover:text-slate-200 rounded px-2 py-1"
                data-testid="escaleta-song-add-text"
              >
                + Añadir «{query.trim()}» como texto (sin enlace)
              </button>
            )}
          </div>
          <button type="button" onClick={() => { setPicking(false); setQuery(''); }} className="text-xs text-slate-500 hover:text-slate-300">Cerrar</button>
        </div>
      ) : (
        <button
          type="button" onClick={() => setPicking(true)}
          className="text-xs text-purple-300 hover:text-purple-200 flex items-center gap-1"
          data-testid="escaleta-add-song"
        >
          <Plus className="w-3.5 h-3.5" /> Añadir canción
        </button>
      )}
    </div>
  );
};

// Etiquetas legibles para el "tipo" de momento especial (valores que devuelve la IA)
const TIPO_MOMENTO_LABELS = {
  baile_novios: 'Baile novios', cierre: 'Cierre', juego: 'Juego',
  entrada: 'Entrada', hora_loca: 'Hora loca', otro: 'Momento especial',
};
const tipoLabel = (tipo) => TIPO_MOMENTO_LABELS[tipo] || tipo;

// Palabras clave por tipo de momento especial, para no duplicar un bloque que la
// escaleta generada (a partir de peticiones) ya representa con otro nombre.
// 'entrada' se deja fuera a propósito: es ambiguo (cóctel vs banquete) y lo resuelve
// mejor el solapamiento de palabras del título concreto del momento.
const TIPO_MOMENTO_KEYWORDS = {
  baile_novios: ['primer baile', 'baile novios', 'baile nupcial', 'vals', 'baile de los novios'],
  cierre: ['cierre', 'despedida', 'ultimo tema', 'ultima cancion', 'final de fiesta'],
  hora_loca: ['hora loca', 'cotillon'],
};
// Palabras genéricas de boda/fase que no deben contar como coincidencia por sí solas
// (muchos momentos distintos ocurren "durante el banquete"; eso no los hace el mismo momento)
const MOMENTO_STOPWORDS = new Set([
  'baile', 'novios', 'entrada', 'fiesta', 'momento', 'especial', 'evento', 'cliente', 'boda',
  'banquete', 'coctel', 'ceremonia', 'salon', 'comedor', 'recepcion', 'bienvenida', 'cena', 'comida', 'discoteca',
]);
// Palabras de espacio/fase usadas SOLO para desambiguar 'entrada' (cóctel vs banquete vs ceremonia)
const ENTRADA_VENUE_WORDS = ['coctel', 'banquete', 'comedor', 'salon', 'ceremonia'];
const normWords = (s) => String(s || '')
  .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
const significantWords = (s) => normWords(s).split(/\s+/).filter((w) => w.length > 3 && !MOMENTO_STOPWORDS.has(w));

// ¿Este momento especial ya está representado por un bloque de la escaleta generada?
// Solo se compara contra el TÍTULO del bloque (no la fase), para no fusionar por
// coincidencia genérica de fase (ej. "Banquete").
const momentoMatchesBlock = (momento, block) => {
  const nbTitulo = normWords(block.titulo);
  const kws = TIPO_MOMENTO_KEYWORDS[momento.tipo] || [];
  if (kws.some((kw) => nbTitulo.includes(kw))) return true;
  if (momento.tipo === 'entrada') {
    const mTitulo = normWords(momento.titulo);
    return ENTRADA_VENUE_WORDS.some((w) => mTitulo.includes(w) && nbTitulo.includes(w));
  }
  const wordsM = significantWords(momento.titulo);
  const wordsB = significantWords(block.titulo);
  return wordsM.length > 0 && wordsM.some((w) => wordsB.includes(w));
};

// Construye un bloque de escaleta a partir de un momento especial (uso compartido
// por el botón individual "+ Escaleta" y por la fusión automática al generar).
const buildMomentoBlock = (m) => ({
  id: uid(),
  _fromMomentoIds: [m.id],
  titulo: m.titulo || '',
  fase: tipoLabel(m.tipo) || '',
  hora: m.cuando || '',
  espacio: '',
  canciones: (m.canciones || []).filter((c) => c.titulo).map((c) => normCancion(c)),
  nota: [m.frase_dj && `🎤 "${m.frase_dj}"`, m.descripcion].filter(Boolean).join(' — '),
});

// Mezcla los "momentos especiales" del evento en un borrador de escaleta ya generado:
// si un momento ya está representado por un bloque existente, enriquece su nota y
// canciones en vez de duplicarlo; si no, lo añade como bloque nuevo. Soporta varios
// momentos fusionados en el mismo bloque (_fromMomentoIds es un array).
const mergeMomentosIntoDraft = (draftBlocks, momentos) => {
  const merged = draftBlocks.map((b) => ({ ...b, canciones: [...(b.canciones || [])] }));
  // Los momentos solo se fusionan contra los bloques ORIGINALES (de peticiones), nunca
  // entre sí, para que dos momentos distintos no se fusionen el uno con el otro por
  // compartir una palabra (ej. dos "Sorpresa ...").
  const baseBlocks = merged.slice(0, draftBlocks.length);
  (momentos || []).forEach((m) => {
    const target = baseBlocks.find((b) => momentoMatchesBlock(m, b));
    if (target) {
      target._fromMomentoIds = [...(target._fromMomentoIds || []), m.id];
      const extra = [m.frase_dj && `🎤 "${m.frase_dj}"`, m.descripcion].filter(Boolean).join(' — ');
      if (extra && !(target.nota || '').includes(extra)) {
        target.nota = [target.nota, extra].filter(Boolean).join(' · ');
      }
      (m.canciones || []).filter((c) => c.titulo).forEach((c) => {
        const dup = target.canciones.some((ec) => normWords(ec.titulo) === normWords(c.titulo));
        if (!dup) target.canciones.push(normCancion(c));
      });
    } else {
      merged.push(buildMomentoBlock(m));
    }
  });
  return merged;
};

/* ---------- Momentos especiales detectados por IA (Texto/WhatsApp/Documentos) ---------- */
const MomentosEspecialesCard = ({ momentos, onAddToEscaleta, escaleta }) => {
  if (!momentos || momentos.length === 0) return null;
  const yaEnEscaleta = (id) => !!id && escaleta.some((b) => (b._fromMomentoIds || []).includes(id));
  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="escaleta-momentos-especiales">
      <CardContent className="p-4 space-y-3">
        <h3 className="text-white font-semibold text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" /> Momentos especiales
          <span className="text-slate-500 font-normal text-sm">({momentos.length})</span>
        </h3>
        <p className="text-xs text-slate-400 -mt-2">
          Detectados por IA desde texto/WhatsApp/documentos del cliente. Pulsa "+ Escaleta" para colocarlos en el
          orden de la noche con su hora exacta.
        </p>
        <div className="space-y-2">
          {momentos.map((m) => {
            const addedAlready = yaEnEscaleta(m.id);
            return (
              <div key={m.id} className="bg-slate-900/50 border border-amber-700/30 rounded p-2" data-testid={`escaleta-momento-especial-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {m.tipo && <span className="bg-amber-700/60 text-amber-100 px-1.5 py-0.5 rounded text-[10px] shrink-0">{tipoLabel(m.tipo)}</span>}
                      <span className="text-white text-sm font-medium">{m.titulo}</span>
                      {m.cuando && <span className="text-slate-500 text-xs">⏰ {m.cuando}</span>}
                    </div>
                    {m.descripcion && <p className="text-slate-300 text-xs mt-1">{m.descripcion}</p>}
                    {m.participantes && <p className="text-slate-500 text-xs mt-0.5">👥 {m.participantes}</p>}
                    {m.frase_dj && <p className="text-emerald-300 text-xs italic mt-0.5">🎤 "{m.frase_dj}"</p>}
                    {m.canciones?.filter((c) => c.titulo).length > 0 && (
                      <p className="text-slate-400 text-xs mt-0.5">
                        ♪ {m.canciones.filter((c) => c.titulo).map((c) => `${c.titulo}${c.artista ? ` - ${c.artista}` : ''}`).join(', ')}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={addedAlready}
                    onClick={() => onAddToEscaleta(m)}
                    className="border-sky-700/60 text-sky-300 hover:bg-sky-600/20 text-xs shrink-0 disabled:opacity-50"
                    data-testid={`escaleta-momento-add-${m.id}`}
                  >
                    {addedAlready ? <Check className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                    {addedAlready ? 'En escaleta' : 'Escaleta'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const EscaletaTab = ({ playlist, onUpdate }) => {
  const [escaleta, setEscaleta] = useState(
    (playlist.escaleta || []).map((b) => ({
      ...b, id: b.id || uid(), canciones: (b.canciones || []).map(normCancion),
    })),
  );
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [dirty, setDirty] = useState(false);
  // Ids de bloques donde el usuario ha pedido ver/editar el campo "Fase"
  // aunque coincida con el Momento (oculto por defecto para no repetir el
  // mismo texto dos veces en los bloques generados). Una vez visible (por
  // ser distinto al Momento), se queda "fijado" aunque luego el Momento se
  // edite para coincidir — así el campo no desaparece mientras se escribe.
  // La comparación ignora mayúsculas/espacios para ocultar también
  // casi-duplicados ("baile" vs "Baile ").
  const faseIsRedundant = (b) => !b.fase || b.fase.trim().toLowerCase() === (b.titulo || '').trim().toLowerCase();
  const [faseRevealed, setFaseRevealed] = useState(
    () => new Set(escaleta.filter((b) => !faseIsRedundant(b)).map((b) => b.id)),
  );
  useEffect(() => {
    setFaseRevealed((prev) => {
      const next = new Set(prev);
      let changed = false;
      escaleta.forEach((b) => {
        if (!faseIsRedundant(b) && !next.has(b.id)) { next.add(b.id); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [escaleta]);
  const audioRef = useRef(null);

  const peticiones = playlist.peticiones || [];

  const playMp3 = async (fileId) => {
    if (playingId === fileId && audioRef.current) {
      audioRef.current.pause(); setPlayingId(null); return;
    }
    try {
      if (audioRef.current) audioRef.current.pause();
      const res = await axios.get(`${API_URL}/audio/${fileId}`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setPlayingId(null);
      await a.play();
      setPlayingId(fileId);
    } catch {
      toast.error('No se pudo reproducir el MP3');
    }
  };

  const updateBlock = (id, field, val) => { setDirty(true); setEscaleta((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: val } : b))); };
  const removeBlock = (id) => { setDirty(true); setEscaleta((prev) => prev.filter((b) => b.id !== id)); };
  const addBlock = () => { setDirty(true); setEscaleta((prev) => [...prev, emptyBlock()]); };

  // Convierte un "momento especial" (detectado por IA en texto/WhatsApp/documentos) en un bloque real de escaleta
  const addMomentoToEscaleta = (m) => {
    if (escaleta.some((b) => (b._fromMomentoIds || []).includes(m.id))) {
      toast.info('Ese momento ya está en la escaleta');
      return;
    }
    setDirty(true);
    setEscaleta((prev) => [...prev, buildMomentoBlock(m)]);
    toast.success(`"${m.titulo}" añadido a la escaleta — ajusta la hora y guarda`);
  };

  // Convierte "HH:MM" (o "H.MM") a minutos para ordenar; las horas de madrugada (00-06) van al final de la noche
  const horaToMin = (h) => {
    const m = /^(\d{1,2})[:.h](\d{2})/.exec(String(h || '').trim());
    if (!m) return Number.POSITIVE_INFINITY; // bloques sin hora al final
    let mins = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    if (mins < 6 * 60) mins += 24 * 60; // 01:30 de madrugada va después de 23:00
    return mins;
  };
  // Sólo tiene sentido reordenar por hora cuando TODOS los bloques tienen una
  // hora real; si algún momento no la tiene, reordenar lo mandaría al final
  // rompiendo el orden canónico Ceremonia → Cóctel → Banquete → Baile.
  const allHaveHora = (blocks) => blocks.length > 0 && blocks.every((b) => horaToMin(b.hora) !== Number.POSITIVE_INFINITY);

  const sortByHora = () => {
    if (!allHaveHora(escaleta)) {
      toast.info('Faltan horas en algunos bloques — se mantiene el orden Ceremonia → Cóctel → Banquete → Baile.');
      return;
    }
    setDirty(true);
    setEscaleta((prev) => [...prev].sort((a, b) => horaToMin(a.hora) - horaToMin(b.hora)));
    toast.success('Bloques ordenados por hora');
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    setDirty(true);
    const items = Array.from(escaleta);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setEscaleta(items);
  };

  const generateIA = async () => {
    if (escaleta.length > 0 && !window.confirm('Esto reemplazará la escaleta actual y se perderán los cambios manuales (horas, orden, notas). Los momentos especiales se volverán a incluir automáticamente. ¿Continuar?')) {
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/playlists/${playlist.id}/generate-escaleta`, {}, { headers: authHeaders() });
      let draft = res.data?.escaleta || [];
      draft = draft.map((b) => ({ ...b, id: b.id || uid(), canciones: (b.canciones || []).map(normCancion) }));
      const momentos = playlist.momentos_especiales_evento || [];
      if (momentos.length > 0) {
        draft = mergeMomentosIntoDraft(draft, momentos);
      }
      if (draft.length === 0) {
        toast.info(res.data?.message || 'No se pudo generar la escaleta.');
        return;
      }
      // Sólo reordena por hora si TODOS los bloques tienen una hora real;
      // si no, se respeta el orden canónico que ya devuelve el backend
      // (Ceremonia → Cóctel → Banquete → Baile).
      if (allHaveHora(draft)) {
        draft.sort((a, b) => horaToMin(a.hora) - horaToMin(b.hora));
      }
      setEscaleta(draft);
      setDirty(true);
      const nMomentos = momentos.filter((m) => draft.some((b) => (b._fromMomentoIds || []).includes(m.id))).length;
      toast.success(
        `Escaleta generada (${draft.length} momentos${nMomentos > 0 ? `, ${nMomentos} especiales incluidos` : ''}) — revísala y guarda.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar la escaleta');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/playlists/${playlist.id}`, { escaleta }, { headers: authHeaders() });
      onUpdate?.(res.data);
      setDirty(false);
      toast.success('Escaleta guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar la escaleta');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await axios.put(`${API_URL}/playlists/${playlist.id}`, { escaleta }, { headers: authHeaders() });
      setDirty(false);
      const res = await axios.get(`${API_URL}/playlists/${playlist.id}/escaleta/pdf`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error('Error al generar el PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="escaleta-tab">
      <EventSummaryCard playlist={playlist} />
      <MomentosEspecialesCard
        momentos={playlist.momentos_especiales_evento}
        escaleta={escaleta}
        onAddToEscaleta={addMomentoToEscaleta}
      />
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-sky-400" /> Escaleta del evento
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                El orden de la noche a partir de TUS peticiones y horarios (sin IA, sin inventar). Cada canción se enlaza a su petición (MP3/Spotify/YouTube). Arrastra para reordenar.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {escaleta.length > 1 && (
                <Button onClick={sortByHora} variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" data-testid="escaleta-sort-hora" title="Reordenar los bloques según la hora que has puesto">
                  <ArrowDownUp className="w-4 h-4 mr-1" /> Ordenar por hora
                </Button>
              )}
              <Button onClick={generateIA} disabled={generating} className="bg-purple-600 hover:bg-purple-700" data-testid="escaleta-generate-ia">
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ListOrdered className="w-4 h-4 mr-1" />}
                {generating ? 'Generando…' : 'Generar escaleta'}
              </Button>
              <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" data-testid="escaleta-pdf">
                {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                Informe PDF
              </Button>
              <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="escaleta-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
              {dirty && (
                <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="escaleta-dirty">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Cambios sin guardar
                </span>
              )}
            </div>
          </div>

          <datalist id="escaleta-fases">{FASES.map((f) => <option key={f} value={f} />)}</datalist>
          <datalist id="escaleta-espacios">
            {(playlist.espacios_evento || []).map((e, i) => (e.espacio ? <option key={i} value={e.espacio} /> : null))}
          </datalist>

          {escaleta.length === 0 && (
            <div className="text-center py-10 text-slate-400 border border-dashed border-slate-700 rounded-lg">
              <ListOrdered className="w-10 h-10 mx-auto mb-2 text-slate-600" />
              Aún no hay escaleta. Pulsa <strong>«Generar escaleta»</strong> para crearla desde tus peticiones o <strong>«Añadir momento»</strong>.
            </div>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="escaleta-list">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                  {escaleta.map((b, index) => (
                    <Draggable key={b.id} draggableId={b.id} index={index}>
                      {(prov, snapshot) => (
                        <div
                          ref={prov.innerRef} {...prov.draggableProps}
                          className={`bg-slate-900/70 border rounded-lg p-3 ${snapshot.isDragging ? 'border-sky-500 shadow-lg' : 'border-slate-700'}`}
                          data-testid={`escaleta-block-${index}`}
                        >
                          <div className="flex items-start gap-2">
                            <div {...prov.dragHandleProps} className="pt-2 text-slate-500 cursor-grab active:cursor-grabbing" data-testid={`escaleta-drag-${index}`}>
                              <GripVertical className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex flex-col sm:flex-row gap-2">
                                <div className="relative sm:w-[90px] shrink-0">
                                  <Clock className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                                  <Input value={b.hora} onChange={(e) => updateBlock(b.id, 'hora', e.target.value)} placeholder="HH:MM" className="bg-slate-800 border-slate-700 text-white text-sm pl-7" data-testid={`escaleta-hora-${index}`} />
                                </div>
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <Input value={b.titulo} onChange={(e) => updateBlock(b.id, 'titulo', e.target.value)} placeholder="Momento (ej: Primer baile)" className="flex-1 min-w-0 bg-slate-800 border-slate-700 text-white text-sm font-medium" data-testid={`escaleta-titulo-${index}`} />
                                  <Button type="button" variant="ghost" onClick={() => removeBlock(b.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9 p-0 shrink-0" data-testid={`escaleta-remove-${index}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!faseIsRedundant(b) || faseRevealed.has(b.id) ? (
                                  <Input list="escaleta-fases" value={b.fase} onChange={(e) => updateBlock(b.id, 'fase', e.target.value)} placeholder="Fase" className="flex-1 min-w-0 bg-slate-800 border-slate-700 text-white text-sm" data-testid={`escaleta-fase-${index}`} />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setFaseRevealed((prev) => new Set(prev).add(b.id))}
                                    className="shrink-0 min-h-9 px-2.5 rounded-md text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                                    data-testid={`escaleta-fase-add-${index}`}
                                  >
                                    + Fase
                                  </button>
                                )}
                                <div className="relative flex-1 min-w-0">
                                  <MapPin className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-cyan-500" />
                                  <Input list="escaleta-espacios" value={b.espacio} onChange={(e) => updateBlock(b.id, 'espacio', e.target.value)} placeholder="Espacio" className="bg-slate-800 border-slate-700 text-white text-sm pl-7" data-testid={`escaleta-espacio-${index}`} />
                                </div>
                              </div>
                              <CancionesEditor
                                canciones={b.canciones}
                                peticiones={peticiones}
                                onChange={(canciones) => updateBlock(b.id, 'canciones', canciones)}
                                onPlay={playMp3}
                                playingId={playingId}
                              />
                              <Input value={b.nota} onChange={(e) => updateBlock(b.id, 'nota', e.target.value)} placeholder="Nota para el DJ (opcional)" className="bg-slate-800 border-slate-700 text-slate-300 text-xs" data-testid={`escaleta-nota-${index}`} />
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Button type="button" variant="outline" onClick={addBlock} className="mt-3 border-sky-700/60 bg-sky-600/10 text-sky-300 hover:bg-sky-600/20" data-testid="escaleta-add-block">
            <Plus className="w-4 h-4 mr-1" /> Añadir momento
          </Button>
        </CardContent>
      </Card>

      <PeticionesPorMomento peticiones={peticiones} onPlay={playMp3} playingId={playingId} />
    </div>
  );
};

export default EscaletaTab;
